(() => {
  if (typeof customElements === 'undefined' || customElements.get('sasha-wall-preview')) return;
  class SashaWallPreview extends HTMLElement {
    connectedCallback() {
      if (this.controller || String(window.Shopify?.theme?.id) !== this.dataset.themeId) return;
      this.hidden = true;
      try {
        const appleDevice = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const anchor = document.createElement('a');
        if (!appleDevice || !anchor.relList?.supports?.('ar') || typeof AbortController !== 'function' || typeof URL.createObjectURL !== 'function' || typeof URL.revokeObjectURL !== 'function') return;
        if (!['config', 'dialog', 'open', 'launch', 'status', 'retry', 'hint', 'launch-label', 'close', 'dimensions', 'print'].every((name) => this.querySelector(`[data-ar-${name}]`))) return;
        this.config = JSON.parse(this.querySelector('[data-ar-config]').textContent);
        this.productRoot = this.closest('sasha-product');
        if (!this.productRoot) return;
        this.dialog = this.querySelector('[data-ar-dialog]');
        if (typeof this.dialog.showModal !== 'function' || typeof this.dialog.close !== 'function') return;
        if (!['loading', 'ready', 'error', 'launch', 'hint', 'framed', 'unframed'].every((name) => typeof this.config?.labels?.[name] === 'string')) return;
        this.controller = new AbortController();
        this.openButton = this.querySelector('[data-ar-open]');
        this.launchLink = this.querySelector('[data-ar-launch]');
        this.status = this.querySelector('[data-ar-status]');
        this.retryButton = this.querySelector('[data-ar-retry]');
        this.platform = 'apple';
        this.dataset.platform = this.platform;
        this.querySelector('[data-ar-hint]').textContent = this.config.labels.hint;
        this.querySelector('[data-ar-launch-label]').textContent = this.config.labels.launch;
        this.launchLink.rel = 'ar';
        const listen = (node, type, handler) => node.addEventListener(type, handler, { signal: this.controller.signal });
        listen(this.openButton, 'click', () => {
          try {
            if (!this.dialog.open) this.dialog.showModal();
            this.track('wall_ar_open');
            this.prepare();
          } catch (error) {
            this.controller.abort();
            this.controller = null;
            this.hidden = true;
            console.error('Sasha wall preview:', error);
          }
        });
        listen(this.querySelector('[data-ar-close]'), 'click', () => this.dialog.close());
        listen(this.launchLink, 'click', () => {
          if (this.dataset.state === 'ready' && !this.launchLink.hidden) this.track('wall_ar_launch');
        });
        listen(this.retryButton, 'click', () => this.prepare());
        listen(this.dialog, 'click', (event) => {
          const rect = this.dialog.getBoundingClientRect();
          if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.dialog.close();
        });
        listen(this.dialog, 'close', () => { this.sequence = (this.sequence || 0) + 1; });
        listen(this.productRoot, 'sasha:variant', () => {
          this.updateAvailability();
          if (this.dialog.open) this.prepare();
        });
        this.updateAvailability();
        this.initAnalytics();
      } catch (error) {
        this.controller?.abort();
        this.controller = null;
        this.hidden = true;
        console.error('Sasha wall preview:', error);
      }
    }
    updateAvailability() {
      let available = false;
      try { this.selection(); available = true; } catch {}
      this.hidden = !available;
      this.openButton.hidden = !available;
      if (!available && this.dialog.open) this.dialog.close();
      if (available) this.track();
    }
    initAnalytics() {
      try {
        const signal = this.controller.signal;
        const available = () => { if (!signal.aborted) this.track(); };
        const later = () => {
          clearTimeout(this.analyticsTimer);
          this.analyticsTimer = setTimeout(available, 1000);
        };
        document.addEventListener('visitorConsentCollected', later, { signal });
        window.addEventListener('load', available, { signal });
        if (!window.Shopify.customerPrivacy && typeof window.Shopify.loadFeatures === 'function') {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => { if (!error) available(); });
        }
        later();
      } catch {}
    }
    track(name) {
      try {
        if (this.hidden || window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true || typeof window.gtag !== 'function') return false;
        const selection = this.selection();
        const send = (event) => window.gtag('event', event, {
          send_to: 'G-4MDJ797Q5F',
          artwork_handle: this.config.handle,
          artwork_size: `${selection.widthCm}x${selection.heightCm}`,
          artwork_frame: selection.framed ? 'black' : 'none'
        });
        if (!this.analyticsAvailableSent) {
          send('wall_ar_available');
          this.analyticsAvailableSent = true;
        }
        if (name) send(name);
        return true;
      } catch { return false; }
    }
    selection() {
      const id = this.productRoot.querySelector('[data-selected-variant-id]')?.value;
      const record = [...this.productRoot.querySelectorAll('[data-variant-record]')].find((node) => node.dataset.variantId === id);
      if (!record) throw new Error('Missing selected variant');
      const match = record.dataset.sizeValue.match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*cm/i);
      if (!match) throw new Error('Unknown size');
      const widthCm = Number(match[1].replace(',', '.'));
      const heightCm = Number(match[2].replace(',', '.'));
      const ratio = widthCm / heightCm;
      if (!Number.isFinite(ratio) || widthCm <= 0 || heightCm <= 0 || (Math.abs(ratio - 0.75) > 0.000001 && Math.abs(ratio - 1) > 0.000001)) throw new Error('Unsupported artwork dimensions');
      if (!Number.isFinite(this.config.imageAspectRatio) || Math.abs(this.config.imageAspectRatio - ratio) > 0.005) throw new Error('Artwork image and print dimensions must have the same ratio');
      return { widthCm, heightCm, framed: record.dataset.isFramed === 'true' };
    }
    async prepare() {
      const sequence = this.sequence = (this.sequence || 0) + 1;
      let timeout;
      this.launchLink.hidden = true;
      this.launchLink.removeAttribute('href');
      this.retryButton.hidden = true;
      this.status.textContent = this.config.labels.loading;
      this.dataset.state = 'loading';
      try {
        const selection = this.selection();
        const key = JSON.stringify(selection);
        const dimensions = `${selection.widthCm} × ${selection.heightCm} cm`;
        this.querySelector('[data-ar-dimensions]').textContent = `${dimensions} · ${this.config.labels[selection.framed ? 'framed' : 'unframed']}`;
        const print = this.querySelector('[data-ar-print]');
        print.style.aspectRatio = `${selection.widthCm} / ${selection.heightCm}`;
        print.style.borderWidth = selection.framed ? `${166 * this.config.frameWidthM / (selection.widthCm / 100)}px` : '0px';
        if (this.preparedKey !== key) {
          const blob = await Promise.race([
            this.modelFor(selection, key, sequence),
            new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error('Wall preview timed out')), 30000); })
          ]);
          if (sequence !== this.sequence || !this.isConnected) return;
          if (this.blobURL) URL.revokeObjectURL(this.blobURL);
          this.blobURL = URL.createObjectURL(blob);
          this.preparedKey = key;
          this.dataset.fileBytes = String(blob.size);
        }
        if (sequence !== this.sequence || !this.dialog.open) return;
        this.launchLink.href = `${this.blobURL}#allowsContentScaling=0`;
        this.launchLink.download = `${this.config.handle}-${selection.widthCm}x${selection.heightCm}-${selection.framed ? 'black-frame' : 'unframed'}-wall-v4.usdz`;
        this.launchLink.hidden = false;
        this.status.textContent = this.config.labels.ready;
        this.dataset.state = 'ready';
      } catch (error) {
        if (sequence !== this.sequence || !this.isConnected) return;
        this.sequence++;
        this.status.textContent = this.config.labels.error;
        this.retryButton.hidden = false;
        this.dataset.state = 'error';
        console.error('Sasha wall preview:', error);
      } finally {
        clearTimeout(timeout);
      }
    }
    async modelFor(selection, key, sequence) {
      while (this.generation && (this.generation.key !== key || this.generation.signal.aborted)) {
        const previous = this.generation;
        await previous.promise.catch(() => {});
        if (sequence !== this.sequence || !this.isConnected) return;
        if (this.generation === previous) this.generation = null;
      }
      if (!this.generation) {
        const signal = this.controller.signal;
        const promise = (async () => {
          this.runtimePromise ||= import(this.dataset.runtime);
          const runtime = await this.runtimePromise.catch((error) => { this.runtimePromise = null; throw error; });
          if (signal.aborted) throw new Error('Wall preview detached');
          return runtime.createArtworkUSDZ({ ...selection, ...this.config });
        })();
        const generation = this.generation = { key, signal, promise };
        promise.catch(() => { if (this.generation === generation) this.generation = null; });
      }
      return this.generation.promise;
    }
    disconnectedCallback() {
      clearTimeout(this.analyticsTimer);
      this.sequence = (this.sequence || 0) + 1;
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) this.dialog.close();
      if (this.blobURL) URL.revokeObjectURL(this.blobURL);
      this.blobURL = null;
      this.preparedKey = null;
    }
  }
  customElements.define('sasha-wall-preview', SashaWallPreview);
})();
